import React from 'react';

import { SingleCharInputControlComponent } from 'design/singleCharInputControl';
import { setCustomTimeout } from 'utils/setCustomTimeout';

type Props = {
  name: string;
  chars: string[];
  disabled?: boolean;
  readOnly?: boolean;
  invalidCharIndexes?: number[];
  autoFocus?: boolean;
  onCharsChange(nextValue: string[]): void;
};

const createCharInputName = (name: string, charIndex: number) =>
  `${name}_${charIndex}`;

const getCharInput = (
  name: string,
  charIndex: number
): HTMLInputElement | void => {
  const elements = document.getElementsByName(
    createCharInputName(name, charIndex)
  );

  for (let element of Array.from(elements)) {
    if (element instanceof HTMLInputElement) {
      return element;
    }
  }
};

const focusCharInput = (name: string, charIndex: number) => {
  const input = getCharInput(name, charIndex);

  if (input) {
    input.focus();
  }
};

const blurCharInput = (name: string, charIndex: number) => {
  const input = getCharInput(name, charIndex);

  if (input) {
    input.blur();
  }
};

export const CharInputs: React.FC<Props> = (props) => {
  const {
    name,
    chars,
    disabled,
    readOnly,
    invalidCharIndexes,
    autoFocus,
    onCharsChange,
  } = props;

  const charIndexes = Array.from(chars.keys());

  const lastCharIndex = charIndexes[charIndexes.length - 1];

  const fillCharsWithText = React.useCallback(
    (startIndex: number, text: string) => {
      const textChars = text ? text.split('') : [''];

      return textChars.reduce(
        (acc, textChar, textCharIndex) => {
          const charIndex = startIndex + textCharIndex;

          if (charIndexes.includes(charIndex)) {
            acc[charIndex] = textChar;
          }

          return acc;
        },
        [...chars]
      );
    },
    [charIndexes, chars]
  );

  const setCharValue = React.useCallback(
    (charIndex: number, charValue: string) => {
      const nextCode = [...chars];

      nextCode[charIndex] = charValue;

      onCharsChange(nextCode);

      const nextCharIndex = charIndex + 1;

      if (!!charValue && charIndexes.includes(nextCharIndex)) {
        focusCharInput(name, nextCharIndex);
      }
    },
    [charIndexes, chars, name, onCharsChange]
  );

  const createNextValueHandler = React.useCallback(
    (charIndex: number) => {
      return (nextValueRaw: string) => {
        const nextValue = nextValueRaw.replace(/\s/g, '');

        const nextChars = fillCharsWithText(charIndex, nextValue);

        onCharsChange(nextChars);

        if (nextValue) {
          const nextCharIndex = charIndex + nextValue.length;

          if (charIndexes.includes(nextCharIndex)) {
            focusCharInput(name, nextCharIndex);
          } else {
            focusCharInput(name, lastCharIndex);

            blurCharInput(name, lastCharIndex);
          }
        } else {
          const prevCharIndex = charIndex - 1;

          if (charIndexes.includes(prevCharIndex)) {
            focusCharInput(name, prevCharIndex);
          } else {
            focusCharInput(name, charIndex);
          }
        }
      };
    },
    [charIndexes, fillCharsWithText, lastCharIndex, name, onCharsChange]
  );

  const createBackspaceHandler = React.useCallback(
    (charIndex: number, charValue: string) => {
      return () => {
        if (!charValue && charIndex > 0) {
          const prevCharIndex = charIndex - 1;

          setCharValue(prevCharIndex, '');

          focusCharInput(name, prevCharIndex);
        }
      };
    },
    [name, setCharValue]
  );

  React.useEffect(() => {
    if (autoFocus) {
      return setCustomTimeout(() => {
        focusCharInput(name, 0);
      }, 0);
    }
  }, [autoFocus, name]);

  return (
    <>
      {chars.map((char, charIndex) => (
        <SingleCharInputControlComponent
          key={charIndex}
          name={createCharInputName(name, charIndex)}
          value={char}
          disabled={disabled}
          readOnly={readOnly}
          invalid={invalidCharIndexes && invalidCharIndexes.includes(charIndex)}
          onValueChange={createNextValueHandler(charIndex)}
          onBackspace={createBackspaceHandler(charIndex, chars[charIndex])}
        />
      ))}
    </>
  );
};
